// Componentes reutilizáveis — Fase 3 da reorganização visual.
// Usam sempre os tokens de src/tokens.js, para que qualquer página que os
// use fique automaticamente consistente com o resto da app.
import React from "react";
import { cor, tipo, raio, sombra, espaco, paleta } from "./tokens";
import { ChevronDown } from "lucide-react";

// ── Cartão — bloco de conteúdo com fundo branco, borda e sombra suave ──
export function Cartao({ children, style, ...props }) {
  return (
    <div
      style={{
        background: cor.branco,
        borderRadius: raio.lg,
        border: `1px solid ${cor.bordaClara}`,
        boxShadow: sombra.sm,
        padding: 22,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

// ── Secção — título de bloco + ação opcional (ex: um botão) à direita ──
export function Seccao({ titulo, acao, children, style }) {
  return (
    <div style={{ marginBottom: espaco(3), ...style }}>
      {(titulo || acao) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: espaco(1.5), gap: espaco(1), flexWrap: "wrap" }}>
          {titulo && <h2 style={tipo.tituloSeccao}>{titulo}</h2>}
          {acao}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Campo — envolve um input/select/textarea com etiqueta e erro ──
export function Campo({ label, erro, obrigatorio, children, style }) {
  return (
    <label style={{ display: "block", marginBottom: espaco(2), ...style }}>
      {label && (
        <span style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: cor.texto }}>
          {label}
          {obrigatorio && <span style={{ color: cor.alerta }}> *</span>}
        </span>
      )}
      {children}
      {erro && <span style={{ display: "block", marginTop: 4, fontSize: 12, color: cor.alerta }}>{erro}</span>}
    </label>
  );
}

// ── Botão — 3 variantes: primário (ação principal), secundário, perigo (destrutivo) ──
const variantesBotao = {
  primario: { background: cor.acaoGradiente, color: "#fff", border: "none" },
  secundario: { background: cor.branco, color: cor.texto, border: `1.5px solid ${cor.borda}` },
  perigo: { background: cor.alerta, color: "#fff", border: "none" },
};

export function Botao({ variante = "primario", children, style, ...props }) {
  const estiloVariante = variantesBotao[variante] || variantesBotao.primario;
  return (
    <button
      style={{
        ...estiloVariante,
        borderRadius: raio.md,
        padding: "10px 20px",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13.5,
        fontWeight: 700,
        cursor: "pointer",
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
}

// ── Bloco resumo — cartão que abre/fecha, com resumo visível quando fechado
// e o conteúdo completo escondido até o utilizador carregar em "Ver tudo".
// Guarda o estado aberto/fechado por utilizador em localStorage (id único
// por bloco). Página Início (Fase 3).
export function BlocoResumo({
  id, icone: Icone, titulo, contador, contadorUrgente,
  resumo, linkPagina, children,
  vazio, vazioMensagem, vazioAcao,
  defaultAberto = false, onToggleChange,
}) {
  const chaveLS = `bloco_${id}`;
  const [aberto, setAberto] = React.useState(() => {
    try {
      const v = window.localStorage.getItem(chaveLS);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch (e) { /* localStorage indisponível — usar valor por defeito */ }
    return defaultAberto;
  });

  const toggle = () => {
    setAberto(a => {
      const novo = !a;
      try { window.localStorage.setItem(chaveLS, novo ? "1" : "0"); } catch (e) { /* ignora */ }
      if (onToggleChange) onToggleChange(novo);
      return novo;
    });
  };

  const cartaoStyle = {
    background: paleta.branco,
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(11,27,61,.06), 0 1px 3px rgba(11,27,61,.08)",
    marginBottom: 24,
    overflow: "hidden",
  };

  if (vazio) {
    return (
      <div style={{ ...cartaoStyle, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: paleta.cinza600, fontSize: 14 }}>
          {Icone && <Icone size={18} strokeWidth={1.75} />}
          {vazioMensagem}
        </div>
        {vazioAcao}
      </div>
    );
  }

  return (
    <div style={cartaoStyle}>
      <button
        onClick={toggle}
        aria-expanded={aberto}
        aria-controls={`bloco-conteudo-${id}`}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          padding: "18px 20px", textAlign: "left", fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {Icone && <Icone size={18} strokeWidth={1.75} color={paleta.cinza900} style={{ flexShrink: 0 }} />}
          <span style={{ fontSize: 16, fontWeight: 600, color: paleta.cinza900 }}>{titulo}</span>
          {contador != null && (
            <span style={{
              background: contadorUrgente ? paleta.perigoFundo : paleta.navy100,
              color: contadorUrgente ? paleta.perigo : paleta.navy900,
              borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700,
              fontVariantNumeric: "tabular-nums", flexShrink: 0,
            }}>{contador}</span>
          )}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, color: paleta.cinza600, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          {aberto ? "Fechar" : "Ver tudo"}
          <ChevronDown size={16} strokeWidth={1.75} style={{ transform: aberto ? "rotate(180deg)" : "none", transition: "transform 180ms ease" }} />
        </span>
      </button>
      {!aberto && (resumo || linkPagina) && (
        <div style={{ padding: "0 20px 20px" }}>
          {resumo}
          {linkPagina}
        </div>
      )}
      <div
        id={`bloco-conteudo-${id}`}
        style={{ display: "grid", gridTemplateRows: aberto ? "1fr" : "0fr", transition: "grid-template-rows 180ms ease" }}
      >
        <div style={{ minHeight: 0, overflow: "hidden" }}>
          <div style={{ padding: "0 20px 20px" }}>
            {children}
            {linkPagina && <div style={{ marginTop: 12 }}>{linkPagina}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Estado vazio — quando uma lista/página não tem dados para mostrar ──
export function EstadoVazio({ icone: Icone, titulo, descricao, acao }) {
  return (
    <div style={{ textAlign: "center", padding: espaco(6), color: cor.textoSecundario }}>
      {Icone && <Icone size={40} strokeWidth={1.5} style={{ marginBottom: espaco(1.5), opacity: 0.5 }} />}
      {titulo && <div style={{ fontSize: 15, fontWeight: 600, color: cor.texto, marginBottom: 4 }}>{titulo}</div>}
      {descricao && <div style={{ fontSize: 13 }}>{descricao}</div>}
      {acao && <div style={{ marginTop: espaco(2) }}>{acao}</div>}
    </div>
  );
}
